import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export const Navbar = () => {
  const { lang, toggle, t } = useLang();
  const { openClient } = useFlow();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "what", label: t.nav.what },
    { id: "how", label: t.nav.how },
    { id: "trust", label: t.nav.trust },
    { id: "pros", label: t.nav.pros },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "py-3" : "py-5"}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10">
        <div className={`flex items-center justify-between rounded-full transition-all duration-500 ${scrolled ? "bg-white/85 backdrop-blur-xl border border-line shadow-[0_8px_30px_rgb(3,33,42,0.06)] px-5 py-2.5" : "px-1 py-1"}`}>
          <button onClick={() => scrollTo("hero")} className="font-heading font-extrabold text-lg tracking-tight text-ink flex items-center gap-2" data-testid="nav-logo">
            <span className="w-2.5 h-2.5 rounded-full bg-forest inline-block" />
            HomeHealthy
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm font-medium text-ink-soft hover:text-ink transition-colors duration-300" data-testid={`nav-${l.id}`}>
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button onClick={toggle} className="hidden sm:flex items-center text-xs font-semibold rounded-full border border-line bg-white overflow-hidden" data-testid="lang-toggle" aria-label="Toggle language">
              <span className={`px-2.5 py-1.5 transition-colors ${lang === "es" ? "bg-forest text-white" : "text-ink-faint"}`}>ES</span>
              <span className={`px-2.5 py-1.5 transition-colors ${lang === "en" ? "bg-forest text-white" : "text-ink-faint"}`}>EN</span>
            </button>

            <button onClick={() => openClient()} className="inline-flex items-center rounded-full bg-forest text-white text-sm font-semibold px-5 py-2.5 hover:bg-sage-deep transition-all duration-300 hover:-translate-y-0.5" data-testid="nav-cta-help">
              {t.nav.help}
            </button>

            <button className="lg:hidden p-2 text-ink" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="lg:hidden mt-3 bg-white/95 backdrop-blur-xl border border-line rounded-3xl p-6 shadow-soft flex flex-col gap-4" data-testid="mobile-menu">
              {links.map((l) => (
                <button key={l.id} onClick={() => { scrollTo(l.id); setOpen(false); }} className="text-left text-base font-medium text-ink-soft hover:text-ink">{l.label}</button>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <button onClick={toggle} className="flex items-center text-xs font-semibold rounded-full border border-line bg-white overflow-hidden">
                  <span className={`px-2.5 py-1.5 ${lang === "es" ? "bg-forest text-white" : "text-ink-faint"}`}>ES</span>
                  <span className={`px-2.5 py-1.5 ${lang === "en" ? "bg-forest text-white" : "text-ink-faint"}`}>EN</span>
                </button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};
